import { useState } from "react";
import { checkConnection } from "../api.js";
import type { ConfigPanelProps } from "./configTypes.js";
import type { DataCheckResult } from "../../shared/types.js";

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** The database a connection page is for: `databases/<name>/connection`. */
function databaseName(path: string): string | undefined {
  return /^databases\/([^/]+)\/connection$/.exec(path)?.[1];
}

/**
 * The "extra UI" for `databases/<name>/connection`: a Check-connection button
 * that probes *this* database (POST /api/data/check → DataSource.ping). A failed
 * probe is a successful check — the error renders inline. Reads nothing from the
 * box directly; it tests the live, resolved connection, so it reports what a
 * query would hit rather than what the unsaved draft says.
 */
export function DatabaseCheckPanel({ path }: ConfigPanelProps) {
  const [result, setResult] = useState<DataCheckResult | null>(null);
  const [busy, setBusy] = useState(false);
  const name = databaseName(path);

  const run = async (): Promise<void> => {
    setBusy(true);
    setResult(null);
    try {
      setResult(await checkConnection(name));
    } catch (e) {
      setResult({ ok: false, error: errMsg(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dw-settings-check">
      <p>
        <button type="button" onClick={() => void run()} disabled={busy}>
          {busy ? "Checking…" : "Check connection"}
        </button>
      </p>
      {result &&
        (result.ok ? (
          <>
            <p className="dw-settings-ok">✓ Connected in {result.elapsedMs} ms</p>
            <p className="dw-muted dw-field-mono">
              {result.database} · {result.user}
            </p>
            <p className="dw-muted dw-field-mono">{result.version}</p>
          </>
        ) : (
          <>
            <p className="dw-settings-fail">✗ Connection failed</p>
            <pre className="dw-error">{result.error}</pre>
          </>
        ))}
    </div>
  );
}
